import type { Profile } from '../core/types';
import { fmtNum } from '../core/util';
import type { State, Store } from '../state/store';
import { useState } from 'preact/hooks';

/**
 * What the file says, as opposed to what was inferred from it. The value-domain choice
 * lives here because it is a property of the file, not of the current view.
 */
export function SourcePanel(
  { store, state, profile, olAlphaBand }:
  { store: Store; state: State; profile: Profile; olAlphaBand: number | null },
) {
  const [divisor, setDivisor] = useState('');
  const r = profile.raster;
  const c = profile.domain.candidates[profile.domain.selected];
  const row = (k: string, v: string, cls?: string) => (
    <div class="kvrow" key={k}><span>{k}</span><b class={cls}>{v}</b></div>
  );
  const setDomain = (i: number) => {
    profile.domain.selected = i;
    store.set({ profile: { ...profile } });
  };
  return (
    <>
      {row('size', `${r.width} × ${r.height}`)}
      {row('bands', String(r.samplesPerPixel))}
      {row('type', `${r.sampleFormatName}${r.bitsPerSample[0]}`)}
      {row('compression', r.compressionName)}
      {row('photometric', r.photometricName)}
      {r.planarConfiguration !== 1 && row('planar', 'separate', 'warn')}
      {row('tiled', r.tiled ? `${r.tileWidth}×${r.tileHeight}` : 'no — stripped',
        r.tiled ? 'ok' : 'bad')}
      {row('overviews', r.overviews.length > 1
        ? r.overviews.slice(1).map((o) => `${o.width}×${o.height}`).join(' ') : 'none',
        r.overviews.length > 1 ? 'ok' : 'bad')}
      {r.maskIFDs > 0 && row('mask IFDs', String(r.maskIFDs), 'ok')}
      {row('nodata', r.nodata == null ? 'not set'
        : fmtNum(r.nodata) + (r.nodataDeclared ? '' : ' (undeclared)'),
        r.nodata != null && !r.nodataDeclared ? 'warn' : undefined)}
      {row('EPSG', String(r.epsg ?? '—'))}
      {r.resolution && row('pixel size',
        `${fmtNum(Math.abs(r.resolution[0]!))} × ${fmtNum(Math.abs(r.resolution[1]!))}`)}
      {r.bbox && row('bounds', r.bbox.map((v) => fmtNum(v)).join(', '))}
      {r.fullResCeiling != null && row('full-res max', fmtNum(r.fullResCeiling))}
      {row('sampled from', profile.sampling.kind === 'overview'
        ? `overview ${profile.sampling.width}×${profile.sampling.height}`
        : `centre window ${profile.sampling.width}×${profile.sampling.height}`)}

      <div class="subhead">value domain</div>
      <select data-testid="domain" value={profile.domain.selected}
        onChange={(e) => setDomain(+(e.target as HTMLSelectElement).value)}>
        {profile.domain.candidates.map((cc, i) => (
          <option key={cc.id} value={i}>{cc.label} — {cc.formula}</option>
        ))}
      </select>
      {c && <p class="hint">{c.note}</p>}

      {/* Escape hatch for depths the classifier has no rule for — 14-bit, 10-bit, odd. */}
      <div class="mandiv">
        <span>or ÷</span>
        <input type="number" step="any" placeholder="16383" value={divisor}
          data-testid="divisor" aria-label="Custom scale divisor"
          onInput={(e) => setDivisor((e.target as HTMLInputElement).value)} />
        <button class="btn tiny" onClick={() => {
          const v = parseFloat(divisor);
          if (!v) return;
          profile.domain.candidates.push({
            id: 'manual', label: `Manual ÷ ${v}`, scale: 1 / v, offset: 0,
            confidence: 'manual', note: 'Divisor entered by hand.', formula: `value ÷ ${v}`,
          });
          profile.domain.selected = profile.domain.candidates.length - 1;
          store.set({ profile: { ...profile } });
        }}>Use</button>
      </div>
      {profile.domain.ambiguous && (
        <div class="note warn">More than one scaling fits this data. Normalized-difference
          indices are unaffected — only true-colour brightness changes.</div>
      )}

      {(r.nodata != null || r.maskIFDs > 0) && (
        <>
          <div class="subhead">nodata mask</div>
          <select data-testid="maskmode" value={state.maskMode}
            onChange={(e) => store.set({ maskMode: (e.target as HTMLSelectElement).value as State['maskMode'] })}>
            <option value="auto">Auto — value and source alpha</option>
            <option value="value" disabled={r.nodata == null}>
              {r.nodata == null ? 'Value test only — no nodata value' : 'Value test only'}</option>
            <option value="alpha" disabled={!olAlphaBand}>
              {olAlphaBand ? 'Source alpha only' : 'Source alpha only — none present'}</option>
            <option value="off">No mask</option>
          </select>
          <p class="hint">
            {r.maskIFDs > 0 && r.nodata == null
              ? olAlphaBand
                ? `This file carries an internal mask, exposed as an alpha band at index ${olAlphaBand}. Masking rests entirely on that.`
                : 'This file carries an internal mask but no nodata value, so masking rests on the source exposing it as an alpha band.'
              : olAlphaBand
                ? `The source exposes an alpha band at index ${olAlphaBand}; both tests are active.`
                : 'No source alpha band, so masking rests on the value test alone.'}
          </p>
        </>
      )}
    </>
  );
}
